import { AUTH_PERSONA_PRESETS } from '@blms/shared/schemas/shiftbook';
import {
  type FormEvent,
  type PropsWithChildren,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { flushWorkspaceDrafts } from '@/features/shiftbook/offline/workspace-draft-flush';
import { prepareWorkspaceRecovery } from '@/features/shiftbook/offline/workspace-recovery';
import { formatUtcIsoInSiteTimeZone, SITE_TIMEZONE } from '@/lib/date-time';
import { buildM365LoginUrl } from '../api/client';
import { useAuth } from '../auth-provider';
import { markPendingAuthFlow } from '../pending-auth-flow';

/** Marks the control that receives focus when a recovery modal opens. */
const AUTOFOCUS_ATTRIBUTE = 'data-autofocus';

function RecoveryModal({
  title,
  description,
  children,
}: PropsWithChildren<{ title: string; description: string }>) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  useLayoutEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', ''); // jsdom has no dialog implementation.
    // showModal() focuses the first focusable control; the flows below want a
    // specific one (password field, primary action) instead.
    dialog.querySelector<HTMLElement>(`[${AUTOFOCUS_ATTRIBUTE}]`)?.focus();
    return () => {
      if (typeof dialog.close === 'function') dialog.close();
    };
  }, []);
  // The native modal layer stays above an entry/attachment dialog, including
  // one that finishes mounting after the expired request. aria-live also keeps
  // existing Radix hideOthers scopes from hiding this recovery surface.
  // pointer-events-auto is load-bearing: an open Radix dialog underneath sets
  // body { pointer-events: none }, which the top-layer dialog would inherit.
  // Visual tokens mirror DialogContent so the surface reads as one of the
  // app's own dialogs rather than a browser prompt.
  return createPortal(
    <dialog
      ref={ref}
      aria-modal="true"
      aria-live="assertive"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onCancel={(event) => event.preventDefault()}
      onClose={() => {
        // Browsers may honour a repeated Escape despite the cancelled cancel
        // event (close-watcher anti-abuse); reopen so the surface stays until
        // the session is recovered and React unmounts it.
        const dialog = ref.current;
        if (dialog?.isConnected && typeof dialog.showModal === 'function') dialog.showModal();
      }}
      className="pointer-events-auto m-auto w-[min(480px,90vw)] rounded-2xl border border-[var(--color-card-border)] bg-[var(--color-bg-secondary)] p-6 text-[var(--color-text-primary)] shadow-[var(--shadow-elevated)] backdrop:bg-[var(--color-bg-overlay,rgba(15,23,42,0.5))] backdrop:backdrop-blur-sm"
    >
      <div className="space-y-2">
        <h2 id={titleId} className="text-lg font-semibold leading-none tracking-tight">
          {title}
        </h2>
        <p id={descriptionId} className="text-sm text-[var(--color-text-secondary)]">
          {description}
        </p>
      </div>
      <div className="mt-6 space-y-4">{children}</div>
    </dialog>,
    document.body,
  );
}

function ErrorNotice({ children }: PropsWithChildren) {
  return (
    <p
      role="alert"
      className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]"
    >
      {children}
    </p>
  );
}

function OrDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 text-xs text-[var(--color-text-tertiary)]" aria-hidden>
      <span className="h-px flex-1 bg-[var(--color-card-border)]" />
      {label}
      <span className="h-px flex-1 bg-[var(--color-card-border)]" />
    </div>
  );
}

const labelClassName = 'block text-xs font-medium text-[var(--color-text-tertiary)]';

type RecoveryError = 'local' | 'preset' | 'microsoft' | 'logout' | null;

export function SessionRecoveryDialog() {
  const { t } = useTranslation('shiftbook');
  const {
    user,
    session,
    loginAsSuperadmin,
    isBusy,
    refreshSession,
    loginWithPreset,
    recoverCustomSession,
    logout,
  } = useAuth();
  const location = useLocation();
  const usernameId = useId();
  const passwordId = useId();
  const [username, setUsername] = useState(user?.username ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<RecoveryError>(null);
  const [waiting, setWaiting] = useState(false);
  const popup = useRef<Window | null>(null);
  const form = useRef<HTMLFormElement>(null);
  const refresh = useRef(refreshSession);
  useLayoutEffect(() => {
    refresh.current = refreshSession;
  }, [refreshSession]);
  const local = user?.sessionSource === 'local' || user?.provider === 'local';
  const preset = AUTH_PERSONA_PRESETS.find((item) => item.id === user?.personaId);
  const showPreset = session.authMode === 'dev' && !local;
  const showMicrosoft = session.authMode === 'm365';
  const redirectTo = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    if (!waiting) return;
    const timer = window.setInterval(() => {
      // Closing/cancelling is harmless: the session endpoint is authoritative.
      if (popup.current?.closed) {
        setWaiting(false);
        void refresh.current();
        return;
      }
      void refresh.current();
    }, 1500);
    return () => {
      window.clearInterval(timer);
      popup.current?.close();
    };
  }, [waiting]);

  const login = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      await loginAsSuperadmin(username, password);
      setPassword('');
    } catch {
      setPassword('');
      setError('local');
    }
  };
  const startMicrosoft = async () => {
    popup.current = window.open(
      'about:blank',
      'blms-session-recovery',
      'popup,width=560,height=720',
    );
    setError(null);
    try {
      prepareWorkspaceRecovery();
      const { durable } = await flushWorkspaceDrafts();
      if (!popup.current && !durable) {
        setError('microsoft');
        return;
      }
    } catch {
      popup.current?.close();
      setError('microsoft');
      return;
    }
    markPendingAuthFlow();
    if (form.current) {
      // A blocked popup falls back to a full-page redirect in this tab; the
      // durability check above ensures memory-only work stays in this tab.
      form.current.action = buildM365LoginUrl(
        popup.current ? '/session-recovery-return.html' : redirectTo,
      );
      form.current.target = popup.current ? 'blms-session-recovery' : '_self';
      form.current.submit();
    }
    setWaiting(Boolean(popup.current));
  };
  return (
    <RecoveryModal title={t('auth.expired.title')} description={t('auth.expired.description')}>
      {local ? (
        <form onSubmit={login} className="space-y-4" aria-busy={isBusy}>
          <div className="space-y-1.5">
            <label htmlFor={usernameId} className={labelClassName}>
              {t('auth.login.superadminUsername')}
            </label>
            <Input
              id={usernameId}
              required
              autoComplete="username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor={passwordId} className={labelClassName}>
              {t('auth.login.superadminPassword')}
            </label>
            <Input
              id={passwordId}
              required
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              data-autofocus=""
            />
          </div>
          {error === 'local' ? <ErrorNotice>{t('auth.login.superadminError')}</ErrorNotice> : null}
          <Button type="submit" className="w-full" disabled={isBusy}>
            {isBusy ? t('auth.login.loggingIn') : t('auth.expired.signIn')}
          </Button>
        </form>
      ) : null}
      {showPreset ? (
        <>
          {error === 'preset' ? <ErrorNotice>{t('auth.login.error')}</ErrorNotice> : null}
          <Button
            className="w-full"
            disabled={isBusy}
            onClick={() => {
              setError(null);
              void (
                !user
                  ? refreshSession()
                  : preset
                    ? loginWithPreset(preset.id)
                    : recoverCustomSession?.()
              )?.catch(() => setError('preset'));
            }}
            data-autofocus=""
          >
            {isBusy ? t('auth.login.loggingIn') : t('auth.expired.signIn')}
          </Button>
        </>
      ) : null}
      {showMicrosoft ? (
        <>
          {local ? <OrDivider label={t('auth.expired.or')} /> : null}
          <form ref={form} method="post" action={buildM365LoginUrl(redirectTo)} hidden />
          {error === 'microsoft' ? <ErrorNotice>{t('auth.expired.error')}</ErrorNotice> : null}
          <Button
            type="button"
            className="w-full"
            variant={local ? 'outline' : 'default'}
            onClick={() => {
              void startMicrosoft();
            }}
            disabled={waiting}
            data-autofocus={local ? undefined : ''}
          >
            {waiting ? t('auth.login.loggingIn') : t('auth.login.m365Action')}
          </Button>
          {waiting ? (
            <p role="status" className="text-xs text-[var(--color-text-secondary)]">
              {t('auth.expired.waitingForMicrosoft')}
            </p>
          ) : null}
        </>
      ) : null}
      {error === 'logout' ? <ErrorNotice>{t('auth.expired.error')}</ErrorNotice> : null}
      <Button
        type="button"
        variant="ghost"
        className="w-full"
        disabled={isBusy}
        onClick={() => {
          void logout().catch(() => setError('logout'));
        }}
      >
        {t('auth.expired.signOutAndDiscard')}
      </Button>
    </RecoveryModal>
  );
}

export function LegacyDraftRecoveryDialog() {
  const { t, i18n } = useTranslation('shiftbook');
  const { legacyDrafts = [], resolveLegacyDrafts } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const resolve = async (action: 'restore' | 'discard') => {
    setBusy(true);
    setError(false);
    try {
      await resolveLegacyDrafts?.(action);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <RecoveryModal
      title={t('auth.legacyDrafts.title')}
      description={t('auth.legacyDrafts.description')}
    >
      <ul className="max-h-64 divide-y divide-[var(--color-card-border)] overflow-auto rounded-lg border border-[var(--color-card-border)] text-sm">
        {legacyDrafts.map((draft) => (
          <li key={draft.key} className="flex items-baseline justify-between gap-3 px-3 py-2">
            <span
              className="min-w-0 truncate font-mono text-xs text-[var(--color-text-secondary)]"
              title={draft.entryId}
            >
              {draft.entryId}
            </span>
            <span className="shrink-0 tabular-nums">
              {draft.savedAt
                ? formatUtcIsoInSiteTimeZone(draft.savedAt, i18n.language, SITE_TIMEZONE)
                : t('auth.legacyDrafts.unknownTime')}
            </span>
          </li>
        ))}
      </ul>
      {error ? <ErrorNotice>{t('auth.legacyDrafts.error')}</ErrorNotice> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => {
            void resolve('discard');
          }}
        >
          {t('auth.legacyDrafts.discard')}
        </Button>
        <Button
          disabled={busy}
          onClick={() => {
            void resolve('restore');
          }}
          data-autofocus=""
        >
          {t('auth.legacyDrafts.restore')}
        </Button>
      </div>
    </RecoveryModal>
  );
}
